import { AnalyticsService } from '@alison/analytics/client';
import { brand } from '@alison/ui';
import type { SvgIconComponent } from '@mui/icons-material';
import CalendarTodayOutlined from '@mui/icons-material/CalendarTodayOutlined';
import CheckOutlined from '@mui/icons-material/CheckOutlined';
import CloseOutlined from '@mui/icons-material/CloseOutlined';
import DoNotDisturbAltOutlined from '@mui/icons-material/DoNotDisturbAltOutlined';
import DonutLargeOutlined from '@mui/icons-material/DonutLargeOutlined';
import ExpandMoreOutlined from '@mui/icons-material/ExpandMoreOutlined';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { type MouseEvent, type ReactNode, useState } from 'react';
import { FILTER_MENU_SLOT_PROPS, filterMenuItemSx } from '../../../filters/menuStyles.js';
import { rangeLabel } from '../../data/format.js';

const OPTIONS: { value: Comparison; label: string; Icon: SvgIconComponent }[] = [
  { value: 'previous', label: 'Previous period', Icon: CalendarTodayOutlined },
  { value: 'account', label: 'Account average', Icon: DonutLargeOutlined },
  { value: 'off', label: 'Off', Icon: DoNotDisturbAltOutlined },
];

export type Comparison = 'previous' | 'account' | 'off';

interface ComparisonPickerProps {
  value: Comparison;
  onChange: (value: Comparison) => void;
  previousDateRange?: readonly [string, string];
}

export function ComparisonPicker({
  value,
  onChange,
  previousDateRange,
}: ComparisonPickerProps): ReactNode {
  const [anchor, setAnchor] = useState<HTMLButtonElement | null>(null);
  const active = value === 'off' ? undefined : OPTIONS.find((option) => option.value === value);
  const description = (option: Comparison): string => {
    if (option === 'previous') {
      return previousDateRange === undefined
        ? 'The same-length period before this range'
        : rangeLabel(previousDateRange);
    }
    if (option === 'account') return 'All creatives in this account';
    return 'Hide comparison deltas';
  };

  return (
    <>
      <Box
        component="button"
        type="button"
        aria-haspopup="menu"
        aria-expanded={anchor !== null}
        onClick={(event: MouseEvent<HTMLButtonElement>) => setAnchor(event.currentTarget)}
        sx={(theme) => ({
          display: 'inline-flex',
          alignItems: 'center',
          gap: 1,
          height: 26,
          pl: 1.5,
          pr: 1,
          border: '1px solid',
          borderColor:
            active === undefined ? theme.palette.border.strong : theme.palette.border.light,
          borderRadius: `${theme.radius.md}px`,
          bgcolor: active === undefined ? 'background.paper' : brand.bgBlue,
          color: active === undefined ? 'text.secondary' : 'primary.main',
          fontSize: 11,
          fontWeight: 600,
          cursor: 'pointer',
          whiteSpace: 'nowrap',
          '&:hover': { borderColor: theme.palette.border.primary },
        })}
      >
        {active === undefined ? (
          'Compare'
        ) : (
          <>
            <Box component="span" sx={{ fontWeight: 500 }}>
              Compare to
            </Box>
            <Box component="span" sx={{ fontWeight: 700 }}>
              {active.label}
            </Box>
          </>
        )}
        <ExpandMoreOutlined sx={{ fontSize: 14, color: 'text.disabled' }} />
        {active === undefined ? null : (
          <Box
            component="span"
            role="button"
            tabIndex={0}
            aria-label="Turn off comparison"
            onClick={(event: MouseEvent<HTMLSpanElement>) => {
              event.stopPropagation();
              onChange('off');
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.stopPropagation();
                event.preventDefault();
                onChange('off');
              }
            }}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 16,
              height: 16,
              borderRadius: 1,
              color: 'text.disabled',
              '&:hover': { bgcolor: 'background.muted', color: 'error.main' },
            }}
          >
            <CloseOutlined sx={{ fontSize: 12 }} />
          </Box>
        )}
      </Box>
      <Menu
        anchorEl={anchor}
        open={anchor !== null}
        onClose={() => setAnchor(null)}
        slotProps={FILTER_MENU_SLOT_PROPS}
      >
        {OPTIONS.flatMap((option) => [
          option.value === 'off' ? <Divider key="divider" sx={{ my: 0.5 }} /> : null,
          <MenuItem
            key={option.value}
            selected={option.value === value}
            onClick={() => {
              AnalyticsService.track('Creative Detail - Change Comparison', {
                comparison: option.label,
              });
              onChange(option.value);
              setAnchor(null);
            }}
            sx={{ ...filterMenuItemSx, py: 1, minHeight: 0 }}
          >
            <option.Icon sx={{ fontSize: 14, color: 'text.secondary' }} />
            <ListItemText
              primary={option.label}
              secondary={description(option.value)}
              sx={{ m: 0, mr: 2 }}
              slotProps={{
                primary: { sx: { fontSize: 12, fontWeight: 600, lineHeight: 1.3 } },
                secondary: { sx: { fontSize: 10, lineHeight: 1.3 } },
              }}
            />
            {option.value === value ? (
              <CheckOutlined sx={{ fontSize: 14 }} color="primary" />
            ) : null}
          </MenuItem>,
        ])}
      </Menu>
    </>
  );
}
